"use client";

import { useEffect } from "react";

/**
 * Exposes the on-screen keyboard height as a CSS custom property
 * `--keyboard-inset` on `<html>`.
 *
 * Why this exists:
 * - Android Chrome honours the `interactive-widget=resizes-content` viewport
 *   meta (set in `src/app/layout.tsx`) and shrinks the layout viewport on
 *   keyboard open, so `100dvh` and sticky footers "just work".
 * - iOS Safari ignores `interactive-widget` and instead overlays the keyboard
 *   on top of the page without resizing the layout viewport. The standard fix
 *   is to observe `window.visualViewport` and expose the difference as a CSS
 *   variable that bottom-anchored UI can consume.
 *
 * Consumers (e.g. the chat composer on /create) read the inset via:
 *   padding-bottom: max(env(safe-area-inset-bottom), var(--keyboard-inset, 0px));
 *
 * The hook is a no-op during SSR and on browsers without `visualViewport`.
 */
export function useVirtualKeyboardInset(): void {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const vv = window.visualViewport;
    if (!vv) return;

    const root = document.documentElement;

    const update = () => {
      const inset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      root.style.setProperty("--keyboard-inset", `${inset}px`);
    };

    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);

    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
      root.style.setProperty("--keyboard-inset", "0px");
    };
  }, []);
}
